import '@/App.css';
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useGA4PageTracking } from './hooks/useGA4PageTracking';

function NavigateWithQuery({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

const Landing = lazy(() => import('./pages/Landing'));
const StartPage = lazy(() => import('./pages/StartPage'));
const BriefRouter = lazy(() => import('./pages/BriefRouter'));
const SummaryPage = lazy(() => import('./pages/brief/SummaryPage'));
const ThankYouPage = lazy(() => import('./pages/brief/ThankYouPage'));
const TermsOfService = lazy(() => import('./pages/policies/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/policies/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./pages/policies/RefundPolicy'));
const DeliveryPolicy = lazy(() => import('./pages/policies/DeliveryPolicy'));
const AboutUs = lazy(() => import('./pages/policies/AboutUs'));
const AIDisclosure = lazy(() => import('./pages/policies/AIDisclosure'));
const AccessPage = lazy(() => import('./pages/AccessPage'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));
const PaymentFailed = lazy(() => import('./pages/PaymentFailed'));
const PricingPage = lazy(() => import('./pages/marketing/PricingPage'));
const StaticCreativesPage = lazy(() => import('./pages/marketing/StaticCreativesPage'));
const VideoCreativesPage = lazy(() => import('./pages/marketing/VideoCreativesPage'));
const BundleCreativesPage = lazy(() => import('./pages/marketing/BundleCreativesPage'));
const FaqPage = lazy(() => import('./pages/marketing/FaqPage'));
const PortfolioPage = lazy(() => import('./pages/marketing/PortfolioPage'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function GA4Tracker() {
  useGA4PageTracking();
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <GA4Tracker />
        <Suspense fallback={
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #F3F0FD',
              borderTopColor: '#5B3FD4',
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite'
            }} />
          </div>
        }>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/static-creatives" element={<StaticCreativesPage />} />
            <Route path="/video-creatives" element={<VideoCreativesPage />} />
            <Route path="/bundle-creatives" element={<BundleCreativesPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/start" element={<StartPage />} />
            <Route path="/brief/summary" element={<SummaryPage />} />
            <Route path="/brief/thankyou" element={<ThankYouPage />} />
            <Route path="/brief/growth" element={<NavigateWithQuery to="/brief/launch_static" />} />
            <Route path="/brief/performance" element={<NavigateWithQuery to="/brief/scale_static" />} />
            <Route path="/brief/engine" element={<NavigateWithQuery to="/brief/dominate_static" />} />
            <Route path="/brief/:plan" element={<BriefRouter />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/delivery" element={<DeliveryPolicy />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/ai-disclosure" element={<AIDisclosure />} />
            <Route path="/access" element={<AccessPage />} />
            <Route path="/order-success/:slug" element={<OrderSuccess />} />
            <Route path="/payment-failed" element={<PaymentFailed />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
